import { FormInstance } from 'antd';
import { forwardRef, useImperativeHandle } from 'react';
import { Card, Column, Form, Input, Row, Select, Spin } from '@bancobarigui/barihaus';
import { MaskedInput } from '@/shared/components/MaskedInput';

import { useAddressForm } from './useAddressForm';

interface AddressFormItemProps {
  form: FormInstance;
}

export interface AddressFormRef {
  resetAddressForm: () => void;
  isCepValidated: () => boolean;
  markCepAsValidated: () => void;
}

export const AddressFormItem = forwardRef<AddressFormRef, AddressFormItemProps>(
  ({ form }, ref) => {
    const {
      cities,
      loading,
      cepError,
      cityOptions,
      stateOptions,
      loadingCities,
      isCepValidated,
      cityApiFailed,
      handleCepChange,
      handleStateChange,
      resetAddressForm,
      markCepAsValidated,
      isAllowManualFill,
    } = useAddressForm({ form });

    useImperativeHandle(ref, () => ({
      resetAddressForm,
      isCepValidated: () => isCepValidated,
      markCepAsValidated,
    }), [isCepValidated, resetAddressForm, markCepAsValidated]);

    return (
      <Card className="relative">
        {loading && (
          <div
            style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(255,255,255,0.5)',
              zIndex: 2,
            }}
          >
            <Spin size="large" />
          </div>
        )}

      <Row gutter={[24, 16]}>
        <Column span={12}>
          <Form.Item
            label="CEP"
            name="postalCode"
            rules={[{ required: true, message: 'Informe o CEP' }]}
            {...(cepError && { validateStatus: isAllowManualFill ? 'warning' as const : 'error' as const, help: cepError })}
          >
            <MaskedInput mask="00000-000" placeholder="XXXXX-XXX" onChange={handleCepChange} />
          </Form.Item>
        </Column>

          <Column span={12}>
            <Form.Item
              label="Logradouro"
              name="street"
              rules={[{ required: true, message: 'Informe o logradouro' }]}
            >
              <Input disabled={!isAllowManualFill} placeholder={!isCepValidated && !isAllowManualFill ? 'Informe um CEP válido' : 'Informe um logradouro'} />
            </Form.Item>
          </Column>

          <Column span={12}>
            <Form.Item
              label="Número"
              name="number"
              rules={[{ required: true, message: 'Informe o número' }]}
            >
              <Input disabled={!isCepValidated && !isAllowManualFill} placeholder={!isCepValidated && !isAllowManualFill ? 'Informe um CEP válido' : 'Ex: 51A'} />
            </Form.Item>
          </Column>

          <Column span={12}>
            <Form.Item label="Complemento" name="complement">
              <Input disabled={!isCepValidated && !isAllowManualFill} placeholder={!isCepValidated && !isAllowManualFill ? 'Informe um CEP válido' : 'Ex: AP 601'} />
            </Form.Item>
          </Column>

          <Column span={12}>
            <Form.Item
              label="Bairro"
              name="district"
              rules={[{ required: true, message: 'Informe o bairro' }]}
            >
              <Input disabled={!isAllowManualFill} placeholder={!isCepValidated && !isAllowManualFill ? 'Informe um CEP válido' : 'Informe um Bairro'} />
            </Form.Item>
          </Column>

          <Column span={12}>
            <Form.Item
              label="Estado"
              name="state"
              rules={[{ required: true, message: 'Informe o estado' }]}
            >
              <Select
                options={stateOptions}
                onChange={handleStateChange}
                disabled={!isAllowManualFill}
                showSearch
                filterOption={(input, option) =>
                  (option?.label?.toString() ?? '').toLowerCase().includes(input.toLowerCase())
                }
                placeholder={!isCepValidated && !isAllowManualFill ? 'Informe um CEP válido' : 'Informe o Estado'}
              />
            </Form.Item>
          </Column>

          <Column span={12}>
            <Form.Item
              label="Município*"
              name="city"
              rules={[{ required: true, message: 'Informe o município' }]}
            >
              {cityApiFailed || (isAllowManualFill && cities.length === 0 && !loadingCities) ? (
                <Input
                  disabled={!isAllowManualFill}
                  placeholder={
                    !isCepValidated && !isAllowManualFill
                      ? 'Informe um CEP válido'
                      : 'Digite o nome do município'
                  }
                />
              ) : (
                <Select
                  options={cityOptions}
                  loading={loadingCities}
                  disabled={!isAllowManualFill}
                  showSearch
                  filterOption={(input, option) => {
                    const label = option?.label || option?.children || option?.value || '';
                    const labelStr = typeof label === 'string' ? label : String(label);
                    return labelStr.toLowerCase().includes(input.toLowerCase());
                  }}
                  placeholder={
                    !isCepValidated && !isAllowManualFill
                      ? 'Informe um CEP válido'
                      : loadingCities
                        ? 'Carregando cidades...'
                        : cities.length === 0
                          ? 'Selecione primeiro o estado'
                          : 'Selecione a cidade'
                  }
                />
              )}
            </Form.Item>
          </Column>
        </Row>
      </Card>
    );
  });

AddressFormItem.displayName = 'AddressFormItem';
